import Joi from 'joi';

export const AccountInfoSchema = Joi.object({
  accountName: Joi.string(),
  partnerId: Joi.number(),
});

export const UserBillingInfoSchema = Joi.object({
  billing_addr1: Joi.string(),
  billing_city: Joi.string(),
  billing_country: Joi.string(),
  billing_state: Joi.string(),
  billing_zip: Joi.string(),
  expiry: Joi.string(),
  firstName: Joi.string(),
  lastFour: Joi.string(),
  lastName: Joi.string(),
  status: Joi.string(),
  type: Joi.string(),
});

export const InvoicesSchema = Joi.array().items(
  Joi.object({
    amount: Joi.number(),
    created_at: Joi.number(),
    downloadUrl: Joi.string(),
    id: Joi.string(),
    status: Joi.string(),
  })
);

export const AddOnItemSchema = Joi.object({
  displayName: Joi.string(),
  name: Joi.string(),
  status: Joi.string(),
  quantity: Joi.number(),
  usage: Joi.number(),
  id: Joi.string(),
});

export const PlanItemSchema = Joi.object({
  controller: Joi.string(),
  groupControllerType: Joi.string(),
  headline: Joi.string(),
  icon: Joi.string(),
  name: Joi.string(),
  position: Joi.string(),
  visible: Joi.boolean(),
});

export const PlanInfoSchema = PlanItemSchema.concat(
  Joi.object({
    controllerValues: Joi.array().items(
      Joi.object({
        key: Joi.string(),
        value: Joi.number(),
      })
    ),
  })
);

export const AddOnsSchema = Joi.object({
  addon_group: Joi.array().items(
    Joi.object({
      group_info: PlanInfoSchema,
      group_items: Joi.array().items(PlanItemSchema),
    })
  ),
});

export const PlanItemInfoSchema = Joi.object({
  addOnItems: Joi.array().items(
    Joi.object({
      created_at: Joi.string(),
      description: Joi.string(),
      kaltura_analytic_ref: Joi.string(),
      license_items: Joi.array().items(
        Joi.object({
          name: Joi.string(),
          value: Joi.string(),
        })
      ),
      name: Joi.string(),
      plan_type: Joi.string(),
      pricing_info: Joi.array().items(
        Joi.object({
          billing_cycle: Joi.string(),
          currency: Joi.string(),
          description: Joi.string(),
          id: Joi.string(),
          price: Joi.number(),
        })
      ),
      product: Joi.string(),
      remote_ref_id: Joi.string(),
      remote_ref_system_id: Joi.string(),
      status: Joi.number(),
      __v: Joi.number(),
      _id: Joi.string(),
    })
  ),
  yearly_addons_info: AddOnsSchema,
  monthly_addons_info: AddOnsSchema,
  addOnPresentationInfo: Joi.array().items(Joi.string()),
  addons: Joi.array().items(AddOnItemSchema),
  billing_cycle: Joi.string(),
  coupon_ids: Joi.array().items(Joi.string()),
  created_at: Joi.string(),
  currency: Joi.string(),
  id: Joi.string(),
  last_run_at: Joi.number(),
  loginUrl: Joi.object({
    url: Joi.string(),
    type: Joi.string(),
    body: Joi.object({
      userId: Joi.string(),
      userKs: Joi.string(),
      ref: Joi.string(),
    }),
  }),
  metadata: Joi.object({
    utm_campaign: Joi.any(),
    utm_medium: Joi.any(),
    utm_source: Joi.any(),
  }),
  next_billing_at: Joi.number(),
  partner_id: Joi.number(),
  plan: Joi.object({
    displayName: Joi.string(),
    name: Joi.string(),
    quantity: Joi.number(),
    usage: Joi.number(),
    visible: Joi.boolean(),
  }),
  planItem: Joi.object({
    created_at: Joi.string(),
    description: Joi.string(),
    license_items: Joi.array().items(
      Joi.object({
        name: Joi.string(),
        value: Joi.string(),
      })
    ),
    name: Joi.string(),
    plan_type: Joi.string(),
    pricing_info: Joi.array().items(
      Joi.object({
        billing_cycle: Joi.string(),
        currency: Joi.string(),
        description: Joi.string(),
        id: Joi.string(),
        price: Joi.number(),
      })
    ),
    product: Joi.string(),
    remote_ref_id: Joi.string(),
    remote_ref_system_id: Joi.string(),
    status: Joi.number(),
    __v: Joi.number(),
    _id: Joi.string(),
  }),
  planPresentationInfo: Joi.string(),
  product: Joi.string(),
  status: Joi.string(),
  subscriptionItemsAddons: Joi.array().items(
    Joi.object({
      displayName: Joi.string(),
      name: Joi.string(),
      quantity: Joi.number(),
      usage: Joi.number(),
    })
  ),
  subscriptionItemsCharge: Joi.array().items(Joi.any()),
  trial_end: Joi.any(),
  type: Joi.string(),
  __v: Joi.number(),
  _id: Joi.string(),
});

export const SubscriptionInfoListSchema = Joi.array().items(PlanItemInfoSchema);

export const SubscriptionOverviewSchema = Joi.object({
  accountInfo: AccountInfoSchema,
  billing_info: UserBillingInfoSchema,
  invoices: InvoicesSchema,
  subscriptionInfoList: SubscriptionInfoListSchema,
  nextInvoiceDate: Joi.string(),
  recurringCharges: Joi.string(),
});

export const CreatePaymentIntentResponseSchema = Joi.object({
  payment_intent: Joi.object({
    active_payment_attempt: Joi.object({
      created_at: Joi.number(),
      id: Joi.string(),
      id_at_gateway: Joi.string(),
      modified_at: Joi.number(),
      object: Joi.string(),
      payment_method_type: Joi.string(),
      status: Joi.string(),
    }),
    amount: Joi.number(),
    created_at: Joi.number(),
    currency_code: Joi.string(),
    customer_id: Joi.string(),
    expires_at: Joi.number(),
    gateway: Joi.string(),
    gateway_account_id: Joi.string(),
    id: Joi.string(),
    modified_at: Joi.number(),
    object: Joi.string(),
    payment_method_type: Joi.string(),
    status: Joi.string(),
  }),
  message: Joi.array().items(Joi.string()),
});

export const BillingInfoSchema = Joi.object({
  address: Joi.string(),
  city: Joi.string(),
  country_code: Joi.string(),
  state_code: Joi.string(),
  zip_code: Joi.string(),
});

export const BillingInfoPayloadSchema = Joi.object({
  token_id: Joi.string(),
  billing_info: BillingInfoSchema,
});

export const CreateCardWithIntentPayloadSchema = Joi.object({
  billing_info: BillingInfoSchema,
  intent_id: Joi.string(),
  gw_token: Joi.string(),
  vat_number: Joi.string(),
});

export const CreateCardWithIntentResponseSchema = Joi.object({
  customer_id: Joi.string(),
  card_info: Joi.object({
    billing_addr1: Joi.string(),
    billing_city: Joi.string(),
    billing_country: Joi.string(),
    billing_state: Joi.string(),
    billing_state_code: Joi.string(),
    billing_zip: Joi.string(),
    brand: Joi.string(),
    expiry_month: Joi.number(),
    expiry_year: Joi.number(),
    first_name: Joi.string(),
    funding_type: Joi.string(),
    iin: Joi.string(),
    last4: Joi.string(),
    last_name: Joi.string(),
    masked_number: Joi.string(),
    object: Joi.string(),
  }),
  message: Joi.array().items(Joi.string()),
  params: Joi.array().items(Joi.string()),
});

export const UpdateBillingAddressPayloadSchema = Joi.object({
  country_code: Joi.string(),
  city: Joi.string(),
  address: Joi.string(),
  zip_code: Joi.string(),
  state_code: Joi.string(),
});
